import { useTheme } from '@emotion/react';
import { OffScreen } from '@icon-park/react';
import { noop } from 'lodash';
import { memo, type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMedia } from 'react-use';

import { Button, Drawer, Tooltip } from '@didaskill/ui/design-system';

import { ReadingTime } from '../reading-time/ReadingTime';
import messages from './LessonDrawer.messages';
import * as styles from './LessonDrawer.styles';

export type LessonDrawerProps = PropsWithChildren<{
  isOpen?: boolean;
  onRequestClose?: () => void;

  estimatedReadingTime?: number;
}>;

const LessonDrawerBase = ({
  isOpen = false,
  onRequestClose = noop,
  estimatedReadingTime = 0,
  children,
}: LessonDrawerProps) => {
  const { breakpoints } = useTheme();

  const isAboveMedium = useMedia(`(min-width: ${breakpoints.medium})`);

  return (
    <Drawer isOpen={isOpen} onRequestClose={onRequestClose} position={Drawer.POSITIONS.FULL_SCREEN}>
      <div css={styles.lessonDrawer}>
        <div css={styles.lessonDrawerHeader}>
          <Tooltip touch={false} content={<FormattedMessage {...messages.CLOSE_DRAWER} />}>
            <Button
              icon={<OffScreen />}
              size={isAboveMedium ? Button.SIZES.LARGE : Button.SIZES.MEDIUM}
              onClick={onRequestClose}
            />
          </Tooltip>

          {estimatedReadingTime > 0 && (
            <ReadingTime estimatedReadingTime={estimatedReadingTime} withIcon={!isAboveMedium} />
          )}

          <div />
        </div>

        <div css={styles.lessonContent}>
          <div css={styles.lessonContentContainer}>{children}</div>
        </div>
      </div>
    </Drawer>
  );
};

LessonDrawerBase.displayName = 'LessonDrawer';

export const LessonDrawer = memo(LessonDrawerBase);
